import * as React from 'react';
import { useOnPageLoaded } from '~/Utils/Hooks/useOnPageLoaded';

const timeToInteractiveThreshold = 5000;
const ensureCoefficient = 1.5; // Purely heuristic value

interface LazyComponentProps<TComponentPropsType> {
    importComponentMethod: () => Promise<React.FC<TComponentPropsType>>;
    componentProps: TComponentPropsType;
    fallbackComponent?: React.ReactNode;
}

export const LazyComponent = <TComponentPropsType extends object>(props: LazyComponentProps<TComponentPropsType>) => {
    const { importComponentMethod, fallbackComponent, componentProps } = props;
    const [Component, setComponent] = React.useState<React.FC<TComponentPropsType> | null>(null);

    useOnPageLoaded(() => {
        // We delay the loading of a component to ensure that the page is already interactive.
        // This is to prevent a component from blocking the rendering of the page and increasing TBT metric.
        setTimeout(async () => {
            const loadedComponent = await importComponentMethod();
            // React executes the function passed to setState, so we cannot store the component directly.
            setComponent(() => loadedComponent);
        }, timeToInteractiveThreshold * ensureCoefficient);
    });

    if (!Component) {
        return fallbackComponent;
    }

    return <Component {...componentProps} />;
};
